import type { OptionTypeBase } from 'react-select';

import { Select, type SelectProps } from '@frontend/legacy-components';

import type { SupportFormFieldName, SupportFormFieldValue } from '../types';
import { SupportFormControl } from './SupportFormControl';
import type { SupportFormControlProps } from './SupportFormControl';
import { SupportFormField } from './SupportFormField';

export type SupportFormSelectFieldProps<
  FieldName extends SupportFormFieldName,
  Option extends OptionTypeBase,
> = Omit<SupportFormControlProps<FieldName>, 'fieldProps' | 'isRequired'> & {
  selectProps: Omit<SelectProps<Option>, 'options'> & { options: Option[] };
};

export const SupportFormSelectField = <
  FieldName extends SupportFormFieldName,
  Option extends OptionTypeBase,
>({
  name,
  selectProps,
  ...restProps
}: SupportFormSelectFieldProps<FieldName, Option>) => {
  return (
    <SupportFormField name={name}>
      {(fieldProps) => {
        const { field, form } = fieldProps;

        const { options, isRequired } = selectProps;

        const getOptionFromFormValue = (
          formValue: SupportFormFieldValue<FieldName>,
        ): Option | undefined =>
          options.find((option) => option.value === formValue);

        const getFormValueFromOption = (
          option: Option | null,
        ): SupportFormFieldValue<FieldName> => option?.value ?? undefined;

        return (
          <SupportFormControl
            {...restProps}
            fieldProps={fieldProps}
            name={name}
            isRequired={isRequired}
          >
            <Select
              {...selectProps}
              name={name}
              value={getOptionFromFormValue(field.value)}
              onChange={(newValue: Option) =>
                form.setFieldValue(field.name, getFormValueFromOption(newValue))
              }
            />
          </SupportFormControl>
        );
      }}
    </SupportFormField>
  );
};
